import React, { useEffect, useState, useCallback } from 'react';
import { getBackendUrl, isDevelopment } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
import { useDrawerHeightTransition } from './hooks/useDrawerHeightTransition';
import { LEGACY_UNBRANDED_FORM_COLOR, useBrandColors, safeFetch } from '@charitystack/shared';
import { useBrandTokenStyle } from '@charitystack/design-tokens/provider';
import EventDetailPage from './EventDetailPage';
import TicketSelectionForm from './TicketSelectionForm';
import FormSkeleton from './Util/FormSkeleton';
import { getStandardizedFormHeight } from './Util/formHeightUtils';
import { buildGroupTicketV2ElementsConfigUrl } from './Util/groupTicketContract';
import { DisplayCurrencyProvider } from './currency/DisplayCurrencyProvider';
import FormSurfacePage from './Components/FormSurfacePage';

/**
 * EventForm Component
 *
 * Displays event registration forms with detail page and ticket selection.
 * Supports two modes:
 *
 * 1. NORMAL MODE (previewMode=false): Fetches data from backend API
 * 2. PREVIEW MODE (previewMode=true): Uses provided previewData for real-time preview
 *
 * Preview Mode Usage:
 * - Used in dashboard edit/create slideouts for live preview
 * - Updates immediately when form fields change
 * - Required previewData fields:
 *   - elementID: Unique identifier for the event
 *   - formName/elementName: Event title
 *   - lightColor/darkColor: Brand colors
 *   - nonprofitName: Organization name
 *   - eventDetails: HTML description
 *   - eventDate, eventStartTime, eventEndTime, eventLocation
 *   - tickets: Array of ticket objects
 *   - customInputs: Array of custom form fields
 *   - formFaqs: Array of FAQ objects
 *   - enableTimeAndLocation, enableEmbedForm, showAnonymousDonations, showOrganizationNameField
 *   - active: Form active status
 */
function EventForm({
  formID,
  isDrawerOpen,
  brandColors,
  backendUrl: propBackendUrl,
  previewMode = false,
  hostedPagePreview = false,
  previewData = null,
  formDisplayMode = 'dynamic',
}) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [currentPage, setCurrentPage] = useState('DETAIL_PAGE');
  const [pageDirection, setPageDirection] = useState(null);
  const [elementID, setElementID] = useState('');
  const [domID, setDomID] = useState('');
  const [backendDataLoaded, setBackendDataLoaded] = useState(false); // Track if backend fetch completed
  const [isReady, setIsReady] = useState(false); // Flag to prevent flicker
  const isHostedPage = useIsHostedPage();

  // Pre-generate brand colors at EventForm level to ensure they're ready before child forms render
  // This prevents the flicker caused by useBrandColors initializing with empty state
  // CRITICAL: Ensure brandColors is a hex string, not an object - brandColors prop might be either
  const brandColorHex = typeof brandColors === 'string' ? brandColors : null;
  // CON-528: no colour is the no-brand set (the legacy "no colour" value), never sage.
  const formColor =
    data?.lightColor ||
    previewData?.lightColor ||
    previewData?.color ||
    brandColorHex ||
    LEGACY_UNBRANDED_FORM_COLOR;
  const { brandColors: generatedBrandColors } = useBrandColors(formColor);
  const areBrandColorsReady = generatedBrandColors && Object.keys(generatedBrandColors).length > 0;
  // CON-514: the CTA and links paint from the brand ROLE tokens, so this form
  // writes them on its own container (inline custom properties — scoped, never
  // documentElement), exactly as EmbedForm does through its provider. Only a
  // CONFIGURED colour brands it; the no-colour value above stays a ramp input.
  const configuredBrandColor =
    data?.lightColor || previewData?.lightColor || previewData?.color || brandColorHex || null;
  const brandTokenStyle = useBrandTokenStyle({
    palette: configuredBrandColor && areBrandColorsReady ? generatedBrandColors : undefined,
  });

  const heightTransitionEnabled = isDrawerOpen || formDisplayMode === 'dynamic';
  const { containerRef: drawerContainerRef, prepareTransition } =
    useDrawerHeightTransition(heightTransitionEnabled);

  // Page transition function - CSS handles crossfade, hook handles drawer height
  const smoothSetCurrentPage = (targetPage, direction = null) => {
    prepareTransition();
    setPageDirection(direction);
    setCurrentPage(targetPage);
  };

  const mergePreviewWithFetchedData = (fetchedData, previewData) => {
    if (!previewData || !fetchedData) {
      return fetchedData || previewData || null;
    }

    // Create merged object starting with fetched data (complete object)
    const merged = { ...fetchedData };

    // Map of preview field names to backend field names (if different)
    const fieldMapping = {
      color: 'lightColor', // preview uses 'color', backend uses 'lightColor'
      convertedContent: 'eventDetails', // preview uses 'convertedContent' for events
      customFAQs: 'faqs', // preview might use customFAQs
      customTickets: 'tickets', // preview uses customTickets
      merchantName: 'nonprofitName', // preview uses merchantName
    };

    // Override with preview data fields
    Object.keys(previewData).forEach((key) => {
      const value = previewData[key];

      // Skip undefined values - we want to keep backend values for these
      if (value === undefined) {
        return;
      }

      // Check if there's a field mapping
      const targetKey = fieldMapping[key] || key;

      // Update the merged object with preview value
      merged[targetKey] = value;

      // For color field, update both lightColor and darkColor
      if (key === 'color') {
        merged.lightColor = value;
        merged.darkColor = value;
      }

      // Handle description fields based on form type
      // For EMBED_FORM: use 'details' field
      // For EVENT: 'convertedContent' maps to 'eventDetails'
      if (key === 'details') {
        merged.details = value;
      }

      if (key === 'convertedContent') {
        if (merged.formType === 'EVENT') {
          merged.eventDetails = value;
          merged.details = value; // Also set details for consistency
        } else {
          // For non-EVENT forms, convertedContent should map to details
          merged.details = value;
        }
      }

      // Handle customFAQs mapping to faqs
      if (key === 'customFAQs') {
        merged.faqs = value;
      }

      // Handle customTickets mapping to tickets
      if (key === 'customTickets') {
        merged.tickets = value;
      }

      // Handle merchantName mapping to nonprofitName
      if (key === 'merchantName') {
        merged.nonprofitName = value;
      }
    });

    // Ensure amountOffset and countOffset are numbers (not strings)
    if (merged.amountOffset && typeof merged.amountOffset === 'string') {
      merged.amountOffset = parseInt(merged.amountOffset, 10) || 0;
    }
    if (merged.countOffset && typeof merged.countOffset === 'string') {
      merged.countOffset = parseInt(merged.countOffset, 10) || 0;
    }
    if (merged.targetAmount && typeof merged.targetAmount === 'string') {
      merged.targetAmount = parseInt(merged.targetAmount, 10) || 0;
    }

    return merged;
  };

  // Memoize fetch function to avoid unnecessary recreations
  const fetchElementsConfig = useCallback(async () => {
    // Use formID from props as elementID for the API call
    let elementID = formID;
    // Store the original formID to check for stale responses
    const originalFormID = formID;
    setLoadError(false);

    // Previews use the authenticated editor's in-memory data, including saved drafts.
    if (previewMode) {
      if (previewData) setData(mergePreviewWithFetchedData({}, previewData));
      setBackendDataLoaded(true);
      return;
    }

    if (elementID === 'NEW_FORM' || !elementID) {
      // Use helper function for development fallback (only in non-preview mode)
      elementID = isDevelopment()
        ? '5010702a-054a-4556-84a6-8e3604edf6ab'
        : 'a9c1354d-0cce-47a7-844e-29f93ee452bf';
    }

    // Use prop backendUrl if provided, otherwise fall back to env variable
    const backendUrl = propBackendUrl || getBackendUrl();

    if (!backendUrl) {
      console.error('❌ EventForm: Backend URL is not configured!');
      console.error('💡 Pass backendUrl as a prop or set VITE_ELEMENTS_BACKEND_URL in environment');
      return;
    }

    try {
      // Use safeFetch with retry logic to handle network errors gracefully
      const result = await safeFetch(
        buildGroupTicketV2ElementsConfigUrl({ backendUrl, elementID }),
        {},
        {
          context: 'Fetching event configuration',
          retries: 2,
          retryDelay: 1000,
        },
      );

      if (!result.ok) {
        throw new Error(result.error || 'API request failed');
      }

      const fetchedData = result.data;

      // RACE CONDITION FIX: Check if formID has changed since we started the fetch
      // If formID changed, this response is stale - ignore it
      if (formID !== originalFormID) {
        return;
      }

      if (fetchedData.elementType !== 'EVENT') {
        throw new Error(`Wrong element type: expected EVENT, got ${fetchedData.elementType}`);
      }

      // If in preview mode, merge preview data with fetched data
      if (previewMode && previewData) {
        const mergedData = mergePreviewWithFetchedData(fetchedData, previewData);
        setData(mergedData);
      } else {
        setData(fetchedData);
      }

      // Mark backend data as loaded
      setBackendDataLoaded(true);
    } catch (error) {
      setLoadError(true);
      console.error('EventForm: Failed to fetch event configuration:', error);
    }
  }, [formID, propBackendUrl, previewMode, previewData]);

  const previewDataString = previewMode && previewData ? JSON.stringify(previewData) : null;

  useEffect(() => {
    if (previewMode && previewData) {
      // Determine if this is a new form (no existing backend data to fetch)
      const isNewFormPreview =
        !previewData.elementID ||
        previewData.elementID === 'preview' ||
        previewData.elementID === 'NEW_FORM';

      // Use functional update to ensure we're working with the latest data state
      setData((currentData) => {
        // For NEW forms: use previewData as base since there's no backend data
        // For EXISTING forms: only use previewData as base if we have NO currentData AND backend hasn't loaded yet
        //   - Once backend loads, we merge with backend data
        //   - Don't overwrite with previewData if currentData is null but backend is loading
        if (!currentData) {
          if (isNewFormPreview) {
            // New form - use preview data directly
            return previewData;
          } else {
            // Existing form - wait for backend data, don't use placeholder previewData
            return null; // Keep null, skeleton will show until backend loads
          }
        }

        // Merge preview data with existing fetched data for real-time updates
        const mergedData = mergePreviewWithFetchedData(currentData, previewData);

        return mergedData;
      });

      setElementID(previewData.elementID || formID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, previewDataString, formID, backendDataLoaded]);

  // Initial setup effect
  useEffect(() => {
    // Reset backend load flag when fetching new configuration
    setBackendDataLoaded(false);

    // Otherwise fetch from backend
    fetchElementsConfig();

    // Extract elementID/domID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const elementID = urlParams.get('elementID') || formID;
    const domID = urlParams.get('domID');

    setElementID(elementID);
    setDomID(domID);

    // If in hosted page, overlay, or drawer context, skip detail page and go directly to form
    if (isHostedPage || formDisplayMode === 'overlay' || isDrawerOpen) {
      setCurrentPage('FORM');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formID, isHostedPage, formDisplayMode, isDrawerOpen, propBackendUrl, previewMode]);

  // Check if embed form is disabled (must be before early return)
  useEffect(() => {
    if (data) {
      // In hosted page preview, hosted page, or overlay context, always show FORM
      if (isHostedPage || hostedPagePreview || formDisplayMode === 'overlay' || isDrawerOpen) {
        setCurrentPage('FORM');
      } else if (data?.enableEmbedForm === true) {
        setCurrentPage('DETAIL_PAGE');
      } else {
        setCurrentPage('FORM');
      }
      // Signal that we're ready to render content
      setIsReady(true);
    }
  }, [data, hostedPagePreview, isHostedPage, formDisplayMode, isDrawerOpen]);

  // Show loading until data is loaded AND brand colors are ready
  // This prevents flicker by keeping skeleton visible during initialization
  // In preview mode for EXISTING forms, wait for backend data to load to ensure complete merged data
  // For NEW forms (no formID, 'preview', or 'NEW_FORM'), don't wait for backend data
  const isNewForm =
    !formID ||
    formID === 'NEW_FORM' ||
    previewData?.elementID === 'preview' ||
    previewData?.elementID === 'NEW_FORM';
  const shouldWaitForBackend = previewMode && !isNewForm && !backendDataLoaded;
  const shouldShowSkeleton = !data || !isReady || shouldWaitForBackend || !areBrandColorsReady;

  if (loadError && !previewMode) {
    return <p role="status" className="p-6 text-center text-[color:var(--cs-text-secondary)]">This form is unavailable.</p>;
  }

  // CON-313: both returns below go through the same provider element, so its
  // state survives the skeleton -> form swap. It renders no DOM node; off in
  // preview mode. CON-750: it publishes its value for the side panel.
  const withCurrency = (node) => (
    <DisplayCurrencyProvider
      config={previewMode ? null : data}
      enabled={!previewMode}
      scopeRef={drawerContainerRef}
      publish={!previewMode}
    >
      {node}
    </DisplayCurrencyProvider>
  );

  if (shouldShowSkeleton) {
    return withCurrency(
      <FormSkeleton
        formType="EVENT"
        config={data || null}
        formDisplayMode={formDisplayMode}
        elementID={elementID}
        domID={domID}
        brandColors={brandColors}
        isDrawerOpen={isDrawerOpen}
      />
    );
  }

  const fixedFormElementHeight = getStandardizedFormHeight({ formDisplayMode });

  return withCurrency(
    <div
      ref={drawerContainerRef}
      id={`${elementID}-CS-event-form`}
      className={`page-container embed-form-responsive-container ${fixedFormElementHeight ? 'form-page-stack' : ''} ${isDrawerOpen ? 'drawer-height-animate' : ''}`}
      data-mode={isDrawerOpen ? 'drawer' : formDisplayMode === 'overlay' ? 'overlay' : 'dynamic'}
      style={{
        ...brandTokenStyle,
        minHeight: formDisplayMode === 'overlay' ? undefined : 'fit-content',
        height: formDisplayMode === 'overlay' ? undefined : 'auto',
      }}
    >
      {/* Detail Page - skip in overlay mode (shown in left pane) and drawer mode */}
      {formDisplayMode !== 'overlay' && !isDrawerOpen && (
        <FormSurfacePage active={currentPage === 'DETAIL_PAGE'} direction={pageDirection} formDisplayMode={formDisplayMode}>
          <EventDetailPage
            data={data}
            setPage={smoothSetCurrentPage}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            isDrawerOpen={isDrawerOpen}
            elementID={elementID}
            domID={domID}
            previewMode={previewMode}
            isPageActive={currentPage === 'DETAIL_PAGE'}
            pageTransitionDirection={pageDirection}
            formDisplayMode={formDisplayMode}
          />
        </FormSurfacePage>
      )}

      {/* Form Page - Always rendered */}
      <FormSurfacePage active={currentPage === 'FORM'} direction={pageDirection} formDisplayMode={formDisplayMode}>
        <TicketSelectionForm
          data={data}
          fetchElementsConfig={fetchElementsConfig}
          isDrawerOpen={isDrawerOpen}
          brandColors={brandColors}
          generatedBrandColors={generatedBrandColors}
          setPage={smoothSetCurrentPage}
          currentPage={currentPage}
          enableEmbedForm={data?.enableEmbedForm}
          previewMode={previewMode}
          hostedPagePreview={hostedPagePreview}
          isPageActive={currentPage === 'FORM'}
          pageTransitionDirection={pageDirection}
          formDisplayMode={formDisplayMode}
        />
      </FormSurfacePage>
    </div>
  );
}

export default EventForm;
